<template>
    <div class="ps-list" style="position: relative">
        <b-loading :is-full-page="false" v-model="isLoading" />

        <template v-if="perfiles.length !== 0">
            <div
                v-for="(grupo, razon_social) in perfilesPorEmpresa"
                :key="razon_social"
                class="ps-grupo"
            >
                <!-- Cabecera de empresa -->
                <div class="ps-empresa-header">
                    <img
                        :src="getCompleteUrl(grupo.ruta_imagen)"
                        :alt="razon_social"
                        class="ps-empresa-logo"
                    />
                    <span class="ps-empresa-nombre">{{ razon_social }}</span>
                </div>

                <!-- Cards de rol -->
                <div class="ps-roles-grid">
                    <button
                        v-for="perfil in grupo.perfiles"
                        :key="perfil.id"
                        class="ps-rol-card"
                        :class="{
                            'ps-rol-card--loggeado': perfil.loggeado,
                            'ps-rol-card--inactivo': !perfil.activo,
                        }"
                        :disabled="perfil.loggeado || !perfil.activo || isLoading"
                        @click="selectPerfil(perfil)"
                    >
                        <span class="ps-rol-nombre">{{ perfil.descripcion }}</span>
                        <span
                            class="ps-rol-badge"
                            :class="{
                                'ps-rol-badge--session': perfil.loggeado,
                                'ps-rol-badge--active': !perfil.loggeado && perfil.activo,
                                'ps-rol-badge--inactive': !perfil.activo,
                            }"
                        >
                            {{
                                perfil.loggeado
                                    ? 'Sesión activa'
                                    : perfil.activo
                                    ? 'Activo'
                                    : 'Inactivo'
                            }}
                        </span>
                    </button>
                </div>
            </div>
        </template>

        <div v-else class="ps-empty">
            <p>No tiene perfiles asignados</p>
        </div>

        <div v-if="errorMsg" class="ps-error">
            <strong>Error</strong>: {{ errorMsg }}
        </div>
    </div>
</template>

<script>
import { BASE_URL } from '@/config';

export default {
    name: 'Perfiles',
    data() {
        return {
            baseUrl: BASE_URL,
            isLoading: false,
            errorMsg: '',
        };
    },
    computed: {
        currentToken() {
            return this.$store.state.auth.token;
        },
        perfiles() {
            return this.$store.state.me.perfiles;
        },
        perfilesPorEmpresa() {
            const grupos = {};
            for (const perfil of this.perfiles) {
                const empresa = perfil.trabajador.empresa;
                const nombre = empresa.razon_social;
                if (!grupos[nombre]) {
                    grupos[nombre] = {
                        ruta_imagen: empresa.ruta_imagen,
                        perfiles: [],
                    };
                }
                grupos[nombre].perfiles.push(perfil);
            }
            return grupos;
        },
    },
    mounted() {
        if (this.perfiles.length === 0) {
            this.fetchPerfiles();
        }
    },
    methods: {
        async fetchPerfiles() {
            this.isLoading = true;
            await this.$store.dispatch('me/getPerfiles');
            this.isLoading = false;
        },
        async selectPerfil(perfil) {
            this.isLoading = true;
            this.errorMsg = '';
            try {
                await this.$store.dispatch('auth/changePerfil', {
                    perfil_id: perfil.id,
                    temporalToken: this.currentToken,
                });
                await this.$store.dispatch(
                    'ui/setisModalSeleccionarPerfil',
                    false
                );
                window.location.replace('/');
            } catch (error) {
                this.isLoading = false;
                this.errorMsg =
                    (error.response && error.response.data.message) ||
                    error.message ||
                    error.toString();
            }
        },
        getCompleteUrl(url) {
            return `${this.baseUrl}/${url}`;
        },
    },
};
</script>

<style scoped>
.ps-list {
    font-family: 'Poppins', 'Segoe UI', sans-serif;
}

/* Grupo por empresa */
.ps-grupo {
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid #d7deea;
}
.ps-grupo:last-child {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
}

/* Cabecera de empresa */
.ps-empresa-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.ps-empresa-logo {
    width: 36px;
    height: 36px;
    object-fit: contain;
    border-radius: 6px;
    background: #f5f7fa;
    padding: 2px;
    border: 1px solid #d7deea;
    flex-shrink: 0;
}

.ps-empresa-nombre {
    font-size: 12px;
    font-weight: 700;
    color: #69778a;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

/* Grid de roles */
.ps-roles-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
}

/* Card de rol */
.ps-rol-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 12px 16px;
    background: #ffffff;
    border: 1.5px solid #d7deea;
    border-radius: 12px;
    cursor: pointer;
    text-align: left;
    transition:
        border-color 0.2s ease,
        background 0.2s ease,
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.ps-rol-card:hover:not(:disabled) {
    border-color: #184289;
    background: rgba(24, 66, 137, 0.04);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(24, 66, 137, 0.1);
}

.ps-rol-card:active:not(:disabled) {
    transform: translateY(0);
}

.ps-rol-card--loggeado {
    background: rgba(24, 66, 137, 0.06);
    border-color: #2d67b4;
    cursor: default;
}

.ps-rol-card--inactivo {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Nombre del rol */
.ps-rol-nombre {
    font-size: 15px;
    font-weight: 600;
    color: #18212f;
    line-height: 1.2;
}

/* Badge de estado */
.ps-rol-badge {
    margin-top: auto;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 20px;
    line-height: 1.6;
    letter-spacing: 0.02em;
}

.ps-rol-badge--active {
    background: rgba(24, 66, 137, 0.1);
    color: #184289;
}

.ps-rol-badge--session {
    background: rgba(24, 66, 137, 0.15);
    color: #184289;
}

.ps-rol-badge--inactive {
    background: rgba(220, 38, 38, 0.08);
    color: #dc2626;
}

/* Sin perfiles */
.ps-empty {
    text-align: center;
    padding: 24px 0;
    color: #69778a;
    font-size: 15px;
}

/* Error inline */
.ps-error {
    margin-top: 16px;
    padding: 10px 14px;
    background: rgba(220, 38, 38, 0.06);
    border: 1px solid rgba(220, 38, 38, 0.2);
    border-radius: 10px;
    font-size: 13px;
    color: #b91c1c;
}
</style>
